import { alpha, TextField, TextFieldProps } from "@mui/material";
import { Palette, styled } from "@mui/material/styles";
import { useMemo } from "react";
import { FeedbackType } from "../types/commonCDSTypes";

type CdsFeedbackTextFieldProps = TextFieldProps & {
  feedbackType?: FeedbackType;
};

export const getBorderColor = (
  feedbackType: FeedbackType | undefined,
  palette: Palette,
  mode: "light" | "dark",
) => {
  switch (feedbackType) {
    case "error":
      return palette.error[mode];
    case "warning":
      return palette.warning[mode];
    case "info":
      return palette.info[mode];
    case "success":
      return palette.success[mode];
    default:
      return palette.action.disabled;
  }
};

export const getCdsTextColor = (
  feedbackType: FeedbackType | undefined,
  palette: Palette,
): string => {
  switch (feedbackType) {
    case "error":
      return palette.error.main;
    case "warning":
      return palette.warning.main;
    case "info":
      return palette.info.main;
    case "success":
      return palette.success.main;
    default:
      return palette.text.primary;
  }
};

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "feedbackType",
})<CdsFeedbackTextFieldProps>(({ theme, feedbackType, disabled }) => {
  const viewOnlyCds = Boolean(disabled && feedbackType);
  const cdsText = getCdsTextColor(feedbackType, theme.palette);
  const neutralOutline = alpha(theme.palette.text.primary, 0.38);

  return {
    "& .MuiOutlinedInput-root": {
      "&.MuiInputBase-root fieldset": {
        borderWidth: feedbackType && !viewOnlyCds ? "2px" : "1px",
        borderColor: viewOnlyCds
          ? `${neutralOutline} !important`
          : getBorderColor(feedbackType, theme.palette, theme.palette.mode),
        ...(viewOnlyCds && { borderStyle: "dashed" }),
      },
    },
    ...(viewOnlyCds && {
      "& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input.Mui-disabled":
        {
          color: `${cdsText} !important`,
          WebkitTextFillColor: `${cdsText} !important`,
          opacity: 1,
        },
    }),
  };
});

const CdsFeedbackTextField = ({
  feedbackType,
  ...props
}: CdsFeedbackTextFieldProps) => {
  const memoizedProps = useMemo(
    () => ({
      feedbackType,
      ...props,
    }),
    [feedbackType, props],
  );

  return <StyledTextField {...memoizedProps} />;
};

CdsFeedbackTextField.displayName = "CdsFeedbackTextField";

export default CdsFeedbackTextField;
